import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { customerApi } from '../services/api';
import { apiErrorCode, apiErrorStatus, isRequestTimeout } from '../utils/apiError';
import { validateInviteFields } from '../utils/inviteValidation';

export interface CustomerInviteForm {
  customerName: string;
  customerEmail: string;
  customerPhone: string;
}

/** What happened when the invitation was sent, for the confirmation. */
export interface CustomerInviteOutcome {
  outcome: 'invited' | 'linked' | 'already_linked';
  /** The finished message to show. */
  message: string;
  customerName: string;
  customerEmail: string;
  emailSent: boolean;
  /** Link the contractor can share themself; only for a new invitation. */
  registrationUrl: string | null;
  /** Invited from a project: the Beställare was linked to it at once. */
  projectLinked: boolean;
}

interface UseCustomerInviteOptions {
  /** Invite to a project without a Beställare (ClickUp 869ff1egy). */
  projectId?: string;
  onInvited?: (outcome: CustomerInviteOutcome) => void;
  /** No answer in time: the invitation may still have been sent, so the
   *  caller can reload what it shows. */
  onTimeout?: () => void;
}

/** A failed request that never got an answer from the server. */
const isNetworkError = (err: unknown): boolean =>
  !!err && typeof err === 'object' && 'request' in err && !apiErrorStatus(err) && !isRequestTimeout(err);

/**
 * Why the invitation could not be sent, in words the Hantverkare can act on,
 * instead of one "try again" for every failure.
 */
export const inviteErrorText = (t: (key: string) => string, err: unknown, toProject: boolean): string => {
  if (isRequestTimeout(err)) return t(toProject ? 'customerInvite.errors.timeoutProject' : 'customerInvite.errors.timeout');
  if (isNetworkError(err)) return t('customerInvite.errors.network');
  if (apiErrorCode(err) === 'PROJECT_CUSTOMER_ALREADY_SET') return t('customerInvite.errors.projectHasCustomer');
  const status = apiErrorStatus(err);
  if (status === 404 && toProject) return t('customerInvite.errors.projectNotFound');
  if (status === 400) return t('customerInvite.errors.invalid');
  if (status === 401 || status === 403) return t('customerInvite.errors.forbidden');
  if (status !== undefined && status >= 500) return t('customerInvite.errors.server');
  return t('customerInvite.error');
};

const EMPTY_FORM: CustomerInviteForm = { customerName: '', customerEmail: '', customerPhone: '' };

/**
 * The invite-a-client form (start page widget, Bjud in beställare and a
 * project without a Beställare; ClickUp 869fj44u8, 869ff1egy): fields,
 * validation, sending and the outcome.
 */
export function useCustomerInvite({ projectId, onInvited, onTimeout }: UseCustomerInviteOptions = {}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<CustomerInviteForm>(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isInviting, setIsInviting] = useState(false);
  const [result, setResult] = useState<CustomerInviteOutcome | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = (): boolean => {
    const next = validateInviteFields(
      { name: form.customerName, email: form.customerEmail, phone: form.customerPhone },
      { name: 'customerName', email: 'customerEmail', phone: 'customerPhone' }
    );
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  /** Sends the (already validated) form; `toProjectId` overrides the option. */
  const send = async (toProjectId = projectId): Promise<CustomerInviteOutcome | null> => {
    setIsInviting(true);
    setResult(null);
    setError(null);

    try {
      const response = await customerApi.inviteCustomer({
        customerName: form.customerName || undefined,
        customerEmail: form.customerEmail,
        customerPhone: form.customerPhone || undefined,
        ...(toProjectId ? { projectId: toProjectId } : {})
      });
      const customerName = response.customerName || form.customerName.trim() || t('customerInvite.theClient');
      const projectLinked = response.projectLinked === true;
      let outcome: CustomerInviteOutcome;
      // A client that is already verified is connected at once: there is
      // nothing to wait for and no link to share (ClickUp 869fj4txv).
      if (response.outcome === 'already_linked') {
        outcome = {
          outcome: 'already_linked', customerName, customerEmail: form.customerEmail.trim(), emailSent: false, registrationUrl: null,
          projectLinked,
          message: t('customerInvite.alreadyLinked', { name: customerName })
        };
      } else if (response.outcome === 'linked') {
        const customerEmail = response.contactEmail || response.customerEmail || form.customerEmail.trim();
        const emailSent = response.emailStatus === 'sent';
        outcome = {
          outcome: 'linked', customerName, customerEmail, emailSent, registrationUrl: null,
          projectLinked,
          message: [
            t('customerInvite.linked', { name: customerName, email: customerEmail }),
            emailSent ? t('customerInvite.linkedEmailSent') : t('customerInvite.linkedEmailFailed')
          ].join(' ')
        };
      } else {
        const emailSent = response.emailStatus === 'sent';
        outcome = {
          outcome: 'invited', customerName, customerEmail: form.customerEmail.trim(), emailSent,
          registrationUrl: response.registrationUrl || null,
          projectLinked,
          message: emailSent ? t('customerInvite.emailSent') : t('customerInvite.emailFailed')
        };
      }
      setResult(outcome);
      setForm(EMPTY_FORM);
      onInvited?.(outcome);
      return outcome;
    } catch (err) {
      console.error('Failed to send invitation:', err);
      setError(inviteErrorText(t, err, !!toProjectId));
      if (isRequestTimeout(err)) onTimeout?.();
      return null;
    } finally {
      setIsInviting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    await send();
  };

  /** Back to an empty form, to invite one more client. */
  const reset = () => {
    setForm(EMPTY_FORM);
    setErrors({});
    setResult(null);
    setError(null);
  };

  return { form, errors, isInviting, result, error, handleChange, handleSubmit, reset, validate, send };
}
